import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, Search, Mail, Phone, AlertOctagon, ShieldAlert, MapPin, Eye, FolderInput, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "./api";
import { PackShipPanel, categoryOf } from "./PackShipPanel";
import { printCenterLink } from "../admin/print/pdfClient";
import { useTuberAudit } from "./useTuberAudit";

const STAGES = {
  all: {
    title: "All Orders",
    subtitle: "Every order in one list, whatever the stage. Click any order to open its full detail page.",
  },
  awaiting_payment: {
    title: "Awaiting Payment",
    subtitle: "Customer has submitted; invoice generated; 48h payment window running. Mark paid to send straight into Packing.",
    next: "assigned", nextLabel: "Mark Paid → Packing",
    chip: "bg-[#A33030] text-white",
    showCountdown: true,
  },
  assigned: {
    title: "Packing (Open)",
    subtitle: "Every order in the Packing stage , paid or not, ready or waiting on holds/clarification. Count = orders, not boxes or parcels. Use “Ready to pack only” for the same rule the Today page uses: paid, no hold, no unreviewed hold note, has an address.",
    next: "shipping", nextLabel: "Ready to dispatch",
    chip: "bg-[#6E1E3A] text-white",
  },
  shipping: {
    title: "On the way · Ready to dispatch & dispatched",
    subtitle: "Ready to dispatch = packed, no tracking yet. Dispatched = Confirm sent recorded with carrier/tracking. Delivered completes the order (no QC wait). Open an order for the fulfilment actions.",
    next: "completed", nextLabel: "Mark delivered (complete)", viaFulfil: "mark_delivered",
    chip: "bg-[#87966B] text-white",
    showShipping: true,
  },
  quality_control: {
    title: "Quality Control",
    subtitle: "48-hour complaint window after delivery. After 48h with no complaint, orders auto-move to Completed.",
    chip: "bg-[#C49247] text-white",
    showQc: true,
  },
  complaint: {
    title: "Customer Complaints",
    subtitle: "Orders with active complaints. Linked to invoice, shipping, photos and admin notes.",
    chip: "bg-[#A33030] text-white",
  },
  completed: {
    title: "Completed Orders",
    subtitle: "Delivered, no complaint within the 48-hour window. Linked to the customer record forever.",
    chip: "bg-[#3C2A1E] text-white",
  },
};

export default function ConsoleOrdersList({ stage }) {
  const meta = STAGES[stage] || STAGES.assigned;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const readyOnly = stage === "assigned" && params.get("ready") === "1";
  const setReadyOnly = (on) => setParams(on ? { ready: "1" } : {});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  // Product filter: a mystery box is a mystery box whether the order came from the store, HQ or a manual import.
  const isMysteryOrder = (r) => r.kind === "mystery_box" || !!r.mystery_tier || (r.line_items || []).some((li) => /mystery/i.test(li.name || "") || li.kind === "mystery_box");
  const [kindFilter, setKindFilter] = useState("all"); // all | manual | vip_buildabox | mystery_box | unpaid | paid
  const [sortBy, setSortBy] = useState("newest");      // newest | oldest | total_desc | total_asc | name
  const [selectedFolders, setSelectedFolders] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/orders", { params: { stage, q } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Couldn't load orders.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [stage]);

  const advance = async (id, target) => {
    setBusy(id);
    try {
      if (STAGES[stage]?.viaFulfil) await api.post(`/admin/orders/${id}/fulfil`, { action: STAGES[stage].viaFulfil });
      else await api.post(`/console/orders/${id}/advance`, { target });
      toast.success(`Moved to ${STAGES[target]?.title || target}.`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't advance.");
    } finally {
      setBusy("");
    }
  };

  const emailReminder = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/console/orders/${id}/email-reminder`, { origin_url: window.location.origin });
      toast.success(`Payment reminder emailed to ${r.data.sent_to}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reminder.");
    } finally { setBusy(""); }
  };


  const counts = useMemo(() => ({
    all: rows.length,
    manual: rows.filter((r) => !r.kind || r.kind === "manual").length,
    vip_buildabox: rows.filter((r) => r.kind === "vip_buildabox").length,
    mystery_box: rows.filter(isMysteryOrder).length,
    unpaid: rows.filter((r) => (r.payment_status || "unpaid") !== "paid").length,
    paid: rows.filter((r) => r.payment_status === "paid").length,
  }), [rows]);

  const visible = useMemo(() => {
    let xs = rows;
    if (kindFilter === "manual")        xs = xs.filter((r) => !r.kind || r.kind === "manual");
    if (kindFilter === "vip_buildabox") xs = xs.filter((r) => r.kind === "vip_buildabox");
    if (kindFilter === "mystery_box")   xs = xs.filter(isMysteryOrder);
    if (kindFilter === "unpaid")        xs = xs.filter((r) => (r.payment_status || "unpaid") !== "paid");
    if (kindFilter === "paid")          xs = xs.filter((r) => r.payment_status === "paid");
    if (readyOnly)                      xs = xs.filter((r) => r.pack_ready);
    const cmp = {
      newest:     (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest:     (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      total_desc: (a, b) => Number(b.total || 0) - Number(a.total || 0),
      total_asc:  (a, b) => Number(a.total || 0) - Number(b.total || 0),
      name:       (a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""),
    }[sortBy];
    return [...xs].sort(cmp || (() => 0));
  }, [rows, kindFilter, sortBy, readyOnly]);

  // Folders act as a filter: pick a folder and the list narrows to it and its
  // orders are selected. Individual orders can also be ticked on their own.
  const shown = useMemo(() => (
    selectedFolders.size === 0 ? visible : visible.filter((o) => selectedFolders.has(categoryOf(o)))
  ), [visible, selectedFolders]);

  // Packing & Shipping: orders sit under their folder heading (Unfiled last).
  const grouped = useMemo(() => {
    if (stage !== "assigned" && stage !== "shipping") return [[null, shown]];
    const map = new Map();
    for (const o of shown) {
      const c = categoryOf(o);
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(o);
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === "Unfiled") return 1;
      if (b[0] === "Unfiled") return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [shown, stage]);

  const toggleFolder = (folder, ids) => {
    const on = selectedFolders.has(folder);
    setSelectedFolders((prev) => {
      const n = new Set(prev);
      if (on) n.delete(folder); else n.add(folder);
      return n;
    });
    setSelectedIds((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => (on ? n.delete(id) : n.add(id)));
      return n;
    });
  };

  const toggleOrder = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const auditQty = useTuberAudit();

  // Low-stock check (Packing only): a variety is short when it's on the tuber
  // audit and the audit count is less than total demand across packing orders.
  const shortVarieties = useMemo(() => {
    if (stage !== "assigned") return new Set();
    const demand = {};
    for (const o of rows) {
      for (const li of o.line_items || []) {
        const key = (li.name || "").trim().toLowerCase();
        if (key) demand[key] = (demand[key] || 0) + Number(li.qty || 1);
      }
    }
    const short = new Set();
    for (const [key, need] of Object.entries(demand)) {
      const have = auditQty(key);
      if (have !== null && have < need) short.add(key);
    }
    return short;
  }, [rows, stage, auditQty]);

  const shortItemsFor = (o) => (o.line_items || [])
    .filter((li) => shortVarieties.has((li.name || "").trim().toLowerCase()))
    .map((li) => li.name);

  const tickTuber = async (o, name, ticked) => {
    try {
      const r = await api.post(`/console/orders/${o.id}/tuber-tick`, { name, ticked });
      setRows((prev) => prev.map((x) => x.id === o.id
        ? { ...x, tuber_ticks: r.data.ticks, packed_at: r.data.all_done ? (x.packed_at || new Date().toISOString()) : (ticked ? x.packed_at : undefined) }
        : x));
      if (r.data.all_done && !o.packed_at) toast.success(`${o.customer_name || "Order"} , every tuber ticked, marked packed.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that tick."); }
  };

  const togglePacked = async (o) => {
    const packed = !o.packed_at;
    setRows((prev) => prev.map((x) => (x.id === o.id ? { ...x, packed_at: packed ? new Date().toISOString() : null } : x)));
    try {
      await api.post(`/console/orders/${o.id}/packed`, { packed });
    } catch {
      toast.error("Couldn't update packed status.");
      load();
    }
  };

  return (
    <div data-testid={`console-orders-${stage}`}>
      <header className="mb-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#6E1E3A] font-semibold">
          Heritage Dahlias · Console
        </div>
        <h1 className="font-serif text-4xl text-[#3C2A1E] mt-1">{readyOnly ? "Ready to pack" : meta.title}</h1>
        <p className="text-[#7A6A55] mt-2 max-w-2xl text-sm">{readyOnly ? `Paid, no hold, no unreviewed hold note, has an address , ${rows.filter((r) => r.pack_ready).length} of ${rows.length} orders in Packing. Blocked orders are listed under “Packing (Open)”.` : meta.subtitle}</p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E8DFCB] rounded flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-[#9C8B73]" />
          <input
            type="text"
            placeholder="Search anything on the order , name, tuber, note, folder, HD number…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="orders-search"
            className="flex-1 outline-none text-sm bg-transparent"
          />
          <button type="button" onClick={load} className="text-[11px] text-[#6E1E3A] hover:underline">Search</button>
        </div>
        <div className="flex items-center gap-1 bg-white border border-[#E8DFCB] rounded p-0.5" aria-label="Filter by kind">
          {[
            { v: "all",            l: `All (${counts.all})` },
            { v: "manual",         l: `Source: manual (${counts.manual})` },
            { v: "vip_buildabox",  l: `Product: VIP box (${counts.vip_buildabox})` },
            { v: "mystery_box",    l: `Product: Mystery box (${counts.mystery_box})` },
            { v: "unpaid",         l: `Payment: unpaid (${counts.unpaid})` },
          ].map((opt) => (
            <button
              key={opt.v} type="button"
              onClick={() => setKindFilter(opt.v)}
              data-testid={`orders-filter-${opt.v}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold rounded ${
                kindFilter === opt.v ? "bg-[#6E1E3A] text-white" : "text-[#3C2A1E] hover:bg-[#FBF6E9]"
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        {stage === "assigned" && (
          <button type="button" onClick={() => setReadyOnly(!readyOnly)} data-testid="orders-filter-ready"
            className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold rounded border ${readyOnly ? "bg-[#2F6B4F] border-[#2F6B4F] text-white" : "bg-white border-[#E8DFCB] text-[#3C2A1E]"}`}>
            Ready to pack only ({rows.filter((r) => r.pack_ready).length})
          </button>
        )}
        {(stage === "assigned" || stage === "shipping") && (
          <Link to={printCenterLink("__batch", [...selectedIds])} data-testid="orders-print-center" className="px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold rounded border border-[#6E1E3A] text-[#6E1E3A] inline-flex items-center gap-1"><Printer className="w-3 h-3" /> Documents & Print{selectedIds.size ? ` (${selectedIds.size})` : ""}</Link>
        )}
        <label className="inline-flex items-center gap-2 bg-white border border-[#E8DFCB] rounded px-2.5 py-1.5">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="orders-sort" className="text-xs bg-transparent outline-none">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="total_desc">Total: high to low</option>
            <option value="total_asc">Total: low to high</option>
            <option value="name">Name A-Z</option>
          </select>
        </label>
      </div>

      {(stage === "assigned" || stage === "shipping") && (
        <PackShipPanel
          orders={visible}
          onReload={load}
          stage={stage}
          selectedFolders={selectedFolders}
          toggleFolder={toggleFolder}
          selectedIds={selectedIds}
        />
      )}

      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : shown.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8DFCB] p-10 text-center text-[#7A6A55] rounded">
          {rows.length === 0 ? (
            <>No orders in <strong>{meta.title}</strong>. {stage === "assigned" && (
              <Link to="/console/orders/create" className="text-[#6E1E3A] hover:underline ml-1">Create the first one →</Link>
            )}</>
          ) : (
            <>No orders match the current filter / search.</>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map(([group, os]) => (
            <React.Fragment key={group || "__flat"}>
          {group && (
            <div className="flex items-center gap-2 pt-3 pb-1" data-testid={`order-group-${group.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <FolderInput className="w-3.5 h-3.5 text-[#6E1E3A]" />
              <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#6E1E3A]">{group}</span>
              <span className="text-[10px] text-[#9C8B73]">{os.length} order{os.length === 1 ? "" : "s"} · {os.filter((o) => o.packed_at).length} packed</span>
              <span className="flex-1 border-t border-dashed border-[#E8DFCB]" />
            </div>
          )}
          {os.map((o) => (
            <article key={o.id} onClick={() => navigate(`/admin/order/${o.id}`)} className={`bg-white border rounded-md p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start cursor-pointer hover:border-[#6E1E3A]/40 hover:shadow-sm transition ${selectedIds.has(o.id) ? "border-[#3E1F46] ring-1 ring-[#3E1F46]/30" : "border-[#E8DFCB]"}`} data-testid={`order-row-${o.id}`}>
              <div className="md:col-span-4">
                {(stage === "assigned" || stage === "shipping") && (
                  <label onClick={(e) => e.stopPropagation()} className="float-right inline-flex items-center gap-1.5 cursor-pointer select-none">
                    <input type="checkbox" checked={selectedIds.has(o.id)} onChange={() => toggleOrder(o.id)}
                      data-testid={`order-select-${o.id}`} className="w-4 h-4 accent-[#3E1F46]" />
                    <span className="text-[9px] tracking-[0.2em] uppercase font-semibold text-[#9C8B73]">Select</span>
                  </label>
                )}
                <div className="font-serif text-[15px] text-[#3C2A1E]">{o.customer_name || "Unknown"}</div>
                <div className="text-xs text-[#7A6A55] flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  {o.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{o.email}</span>}
                  {o.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{o.phone}</span>}
                </div>
                {o.address && (
                  <div className="text-xs text-[#7A6A55] mt-1 flex items-start gap-1">
                    <MapPin className="w-3 h-3 mt-0.5" /> {
                      typeof o.address === "string"
                        ? o.address
                        : [o.address.line1, o.address.line2, o.address.suburb, o.address.city, o.address.postcode, o.address.region]
                            .filter(Boolean).join(", ")
                    }
                  </div>
                )}
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73] mt-1">
                  {o.order_number ? `#${o.order_number}` : `#${o.id?.slice(0, 8)}`} · {new Date(o.created_at).toLocaleDateString("en-NZ")}
                  {stage === "all" && (
                    <span className="ml-2 px-1.5 py-0.5 bg-[#3C2A1E] text-white rounded" data-testid={`order-stage-${o.id}`}>{(o.stage || "").replace("_", " ")}</span>
                  )}
                </div>
                {o.kind === "online_store" && (
                  <div className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[9px] tracking-[0.25em] uppercase font-semibold bg-[#2F6B4F] text-white rounded" data-testid={`order-kind-${o.id}`}>
                    Online Store
                  </div>
                )}
                {o.kind === "vip_buildabox" && (
                  <div className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[9px] tracking-[0.25em] uppercase font-semibold bg-[#6E1E3A] text-white rounded" data-testid={`order-kind-${o.id}`}>
                    VIP Build-a-Box
                  </div>
                )}
                {stage === "shipping" && (
                  <span data-testid={`dispatch-state-${o.id}`} className={"text-[9px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded " + (o.shipped_at || o.shipping_status === "dispatched" || (o.tracking_number && o.shipping_status && o.shipping_status !== "tracking-assigned") ? "bg-[#1A73E8] text-white" : "bg-[#E8DFCB] text-[#3C2A1E]")}>
                    {o.shipped_at || o.shipping_status === "dispatched" ? "Dispatched" : o.tracking_number ? "Tracking recorded · not confirmed sent" : "Ready to dispatch"}
                  </span>
                )}
                {o.hold && (
                  <span data-testid={`order-hold-badge-${o.id}`} title={o.hold.reason} className="text-[9px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded bg-[#A33030] text-white">On hold</span>
                )}
                {o.legacy_hold_note && (
                  <span data-testid={`order-legacy-hold-${o.id}`} title={o.legacy_hold_note} className="ml-1 text-[9px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded bg-[#C49247] text-white">Hold note , review</span>
                )}
                {stage === "assigned" && !o.pack_ready && (o.pack_blockers || []).filter((b) => b.code !== "hold" && b.code !== "legacy_hold_note").map((b) => (
                  <span key={b.code} data-testid={`order-blocker-${b.code}-${o.id}`} className="ml-1 text-[9px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded bg-[#E8DFCB] text-[#3C2A1E]">{b.label}</span>
                ))}
                {o.kind === "mystery_box" && (
                  <div className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[9px] tracking-[0.25em] uppercase font-semibold bg-[#C49247] text-white rounded" data-testid={`order-kind-${o.id}`}>
                    Mystery Box
                  </div>
                )}
                {o.pack_folder && (
                  <div className="mt-1 ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-[0.25em] uppercase font-semibold bg-[#3E1F46] text-white rounded" data-testid={`order-folder-${o.id}`}>
                    <FolderInput className="w-2.5 h-2.5" /> {o.pack_folder}
                  </div>
                )}
                {(stage === "assigned" || stage === "shipping") && (
                  <>
                    <button type="button" onClick={(e) => { e.stopPropagation(); togglePacked(o); }} data-testid={`order-packed-${o.id}`}
                      className={`mt-1.5 block px-2.5 py-1 rounded border text-[10px] tracking-[0.15em] uppercase font-bold transition-colors ${o.packed_at ? "bg-[#2F6B4F] border-[#2F6B4F] text-white" : "bg-white border-[#E8DFCB] text-[#7A6A55] hover:border-[#2F6B4F] hover:text-[#2F6B4F]"}`}>
                      {o.packed_at ? "✓ Packed" : "Mark packed"}
                    </button>
                    <div className="mt-1.5 flex gap-1">
                      <button type="button" onClick={(e) => { e.stopPropagation(); navigate(printCenterLink("worksheet", [o.id])); }} data-testid={`order-print-sheet-${o.id}`}
                        title="Packing worksheet (all notes & picks) , opens Documents & Print"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E8DFCB] bg-white text-[9px] tracking-[0.12em] uppercase font-bold text-[#6E1E3A] hover:border-[#6E1E3A]">
                        <Printer className="w-3 h-3" /> Sheet
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); navigate(printCenterLink("invoice", [o.id])); }} data-testid={`order-print-invoice-${o.id}`}
                        title="Invoice / paid receipt , opens Documents & Print"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E8DFCB] bg-white text-[9px] tracking-[0.12em] uppercase font-bold text-[#6E1E3A] hover:border-[#6E1E3A]">
                        <Printer className="w-3 h-3" /> Invoice
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); navigate(printCenterLink("address_labels", [o.id])); }} data-testid={`order-print-label-${o.id}`}
                        title="Address label , opens Documents & Print"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E8DFCB] bg-white text-[9px] tracking-[0.12em] uppercase font-bold text-[#6E1E3A] hover:border-[#6E1E3A]">
                        <Printer className="w-3 h-3" /> Label
                      </button>
                    </div>
                  </>
                )}
                {stage === "assigned" && shortItemsFor(o).length > 0 && (
                  <div className="mt-1.5 inline-flex items-start gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] bg-[#A33030] text-white rounded" data-testid={`order-low-stock-${o.id}`}>
                    <ShieldAlert className="w-3 h-3 mt-px shrink-0" /> Low stock: {shortItemsFor(o).join(", ")}
                  </div>
                )}
                {meta.showCountdown && o.payment_due_at && (
                  <PaymentCountdown deadline={o.payment_due_at} orderId={o.id} />
                )}
              </div>

              <div className="md:col-span-3 text-xs text-[#3C2A1E]">
                <div className="space-y-0.5">
                  {(o.line_items || []).slice(0, 4).map((li, i) => (
                    <div key={i}>{li.qty}× {li.name}</div>
                  ))}
                  {(o.line_items || []).length > 4 && <div className="text-[#9C8B73]">+ {o.line_items.length - 4} more…</div>}
                </div>
                {Number(o.hq_pick_count || 0) > 0 && (
                  <div className="mt-1 text-[#6E1E3A] text-[11px]">
                    <strong>{o.hq_pick_count}</strong> HQ tuber pick{o.hq_pick_count === 1 ? "" : "s"}
                    {(o.hq_picked_variety_ids || []).length > 0 ? (
                      <span className="ml-1.5 text-green-800 font-semibold" data-testid={`picks-in-${o.id}`}>✓ customer picks in</span>
                    ) : (
                      <span className="ml-1.5 text-[#A33030] italic" data-testid={`picks-waiting-${o.id}`}>· awaiting customer picks</span>
                    )}
                  </div>
                )}
                {o.hq_picks_note && (
                  <div className="mt-1 text-[#6E1E3A] italic text-[11px]">HQ note: {o.hq_picks_note}</div>
                )}
                {(stage === "assigned" || stage === "shipping") && (o.tubers || []).length > 0 && (
                  <div className="mt-1.5 px-2 py-1.5 rounded border border-[#C9A268] bg-[#FBF6E9] text-[11px] text-[#3C2A1E]" data-testid={`order-tubers-${o.id}`}>
                    <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-[#6E1E3A]">
                      Tubers to pull · {o.tubers.reduce((n, t) => n + Number(t.qty || 0), 0)}
                      {stage === "assigned" && <span className="ml-2 normal-case tracking-normal font-normal text-[#9C8B73]">{Object.keys(o.tuber_ticks || {}).length}/{o.tubers.length} ticked</span>}
                    </span>
                    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      {o.tubers.map((t) => {
                        const done = !!(o.tuber_ticks || {})[t.name.trim().toLowerCase()];
                        return stage === "assigned" ? (
                          <label key={t.name} onClick={(e) => e.stopPropagation()} className={`inline-flex items-center gap-1 cursor-pointer ${done ? "line-through opacity-60" : ""}`}>
                            <input type="checkbox" checked={done} onChange={(e) => tickTuber(o, t.name, e.target.checked)}
                              className="accent-[#2F6B4F]" data-testid={`tuber-tick-${o.id}-${t.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                            <strong>{t.qty}×</strong> {t.name}
                          </label>
                        ) : <span key={t.name}><strong>{t.qty}×</strong> {t.name}</span>;
                      })}
                    </div>
                  </div>
                )}
                <div className="mt-2 font-semibold">Total NZ${Number(o.total || 0).toFixed(2)}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase mt-0.5">
                  <span className={`px-1.5 py-0.5 rounded ${
                    o.payment_status === "paid" ? "bg-[#2F4A2A] text-white" :
                    o.payment_status === "partial" ? "bg-[#C49247] text-white" : "bg-[#A33030]/15 text-[#A33030]"
                  }`}>
                    {o.payment_status || "unpaid"}
                  </span>
                </div>
              </div>

              <div className="md:col-span-5 flex flex-wrap gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/order/${o.id}`)}
                  data-testid={`open-drawer-${o.id}`}
                  className="px-3 h-9 text-xs tracking-[0.15em] uppercase font-semibold bg-white border border-[#E8DFCB] text-[#3C2A1E] hover:bg-[#FBF6E9] rounded inline-flex items-center gap-1.5"
                  title="Open full order detail"
                >
                  <Eye className="w-3.5 h-3.5" /> Open
                </button>
                {stage === "awaiting_payment" && o.payment_status !== "paid" && o.email && (
                  <button
                    type="button"
                    disabled={busy === o.id}
                    onClick={() => emailReminder(o.id)}
                    data-testid={`email-reminder-${o.id}`}
                    title={o.reminder_last_sent_at ? `Last reminder: ${new Date(o.reminder_last_sent_at).toLocaleDateString("en-NZ")}` : "Email a payment reminder with pay link"}
                    className="px-3 h-9 text-xs tracking-[0.15em] uppercase font-semibold bg-white border border-[#C49247] text-[#C49247] hover:bg-[#C49247] hover:text-white rounded inline-flex items-center gap-1.5"
                  >
                    <Mail className="w-3.5 h-3.5" /> Remind{Number(o.reminder_count || 0) > 0 ? ` (${o.reminder_count})` : ""}
                  </button>
                )}
                {meta.showQc && (
                  <QcRow o={o} onMarkComplaint={() => navigate(`/console/orders/${o.id}/complaint`)} />
                )}
                {meta.next && (
                  <button
                    type="button"
                    disabled={busy === o.id}
                    onClick={() => advance(o.id, meta.next)}
                    data-testid={`advance-${o.id}`}
                    className="px-3 h-9 text-xs tracking-[0.15em] uppercase font-semibold bg-[#3C2A1E] hover:bg-[#26190E] text-white rounded inline-flex items-center gap-1.5"
                  >
                    {busy === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                    {meta.nextLabel}
                  </button>
                )}
                {stage !== "complaint" && stage !== "completed" && (
                  <button
                    type="button"
                    onClick={() => advance(o.id, "complaint")}
                    className="px-2 h-9 text-[11px] text-[#A33030] border border-[#A33030]/30 rounded hover:bg-[#A33030]/5 inline-flex items-center gap-1"
                    data-testid={`flag-complaint-${o.id}`}
                    title="Flag a customer complaint"
                  >
                    <AlertOctagon className="w-3 h-3" /> Complaint
                  </button>
                )}
              </div>
            </article>
          ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}


function QcRow({ o, onMarkComplaint }) {
  const deadline = o.qc_deadline ? new Date(o.qc_deadline) : null;
  const hoursLeft = deadline ? Math.max(0, Math.floor((deadline.getTime() - Date.now()) / 3600000)) : null;
  return (
    <div className="flex flex-col gap-1 items-end">
      {deadline && (
        <div className="text-[11px]">
          <ShieldAlert className="w-3 h-3 inline-block text-[#C49247]" /> 48h window: <strong className={hoursLeft > 0 ? "text-[#C49247]" : "text-[#2F4A2A]"}>{hoursLeft > 0 ? `${hoursLeft}h left` : "expired"}</strong>
        </div>
      )}
      <button type="button" onClick={onMarkComplaint} className="px-3 py-1.5 text-[11px] bg-[#A33030] text-white rounded hover:bg-[#8b2727]" data-testid={`open-complaint-${o.id}`}>
        + Lodge complaint
      </button>
    </div>
  );
}

function PaymentCountdown({ deadline, orderId }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => { clearInterval(id); };
  }, []);
  const due = new Date(deadline).getTime();
  if (Number.isNaN(due)) return null;
  const msLeft = due - now;
  if (msLeft <= 0) {
    return (
      <span className="mt-1 inline-block text-[10px] tracking-[0.25em] uppercase font-semibold text-[#A33030]" data-testid={`payment-expired-${orderId}`}>
        Payment expired
      </span>
    );
  }
  const hours = Math.floor(msLeft / 3600000);
  const mins = Math.floor((msLeft % 3600000) / 60000);
  const urgent = hours < 6;
  return (
    <span
      className={`mt-1 inline-block text-[10px] tracking-[0.25em] uppercase font-semibold ${urgent ? "text-[#A33030]" : "text-[#C49247]"}`}
      data-testid={`payment-countdown-${orderId}`}
    >
      {hours}h {mins.toString().padStart(2, "0")}m left
    </span>
  );
}

